<template>
  <div class="space-y-6">
    <!-- Quick Create from Template -->
    <div v-if="templates.length > 0" class="bg-white rounded-lg border p-6">
      <h3 class="text-lg font-semibold mb-4">Create from Template</h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          v-for="template in templates"
          :key="template.id"
          @click="createFromTemplate(template)"
          class="text-left p-4 border-2 border-gray-200 rounded-lg hover:border-tb-blue hover:shadow-md transition-all group"
        >
          <h4 class="font-semibold text-gray-900 group-hover:text-tb-blue">{{ template.name }}</h4>
          <p class="text-sm text-gray-500 mt-1 line-clamp-2">{{ template.description || 'No description' }}</p>
          <span class="text-xs text-gray-400 mt-2 inline-block">{{ template.fields_config?.length || 0 }} fields</span>
        </button>
      </div>
    </div>

    <!-- Forms List -->
    <div class="bg-white rounded-lg border p-6">
      <div class="mb-6">
        <h3 class="text-2xl font-semibold">Lead Intake Forms</h3>
      </div>

      <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <LoadingSpinner color-class="text-tb-blue"></LoadingSpinner>
    </div>

    <!-- Empty State -->
    <div v-else-if="forms.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No forms yet</h3>
      <p class="mt-1 text-sm text-gray-500">Use a template above to create a form for this lead.</p>
    </div>

    <!-- Forms List -->
    <div v-else class="space-y-4">
      <div
        v-for="form in forms"
        :key="form.id"
        class="border rounded-lg p-4 hover:shadow-md transition-shadow"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <div class="flex items-center space-x-3">
              <h4 class="text-lg font-semibold">{{ form.name }}</h4>
              <span
                :class="[
                  'px-2 py-1 text-xs font-medium rounded-full',
                  form.status === 'active' ? 'bg-green-100 text-green-800' :
                  form.status === 'expired' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                ]"
              >
                {{ form.status }}
              </span>
            </div>
            <p v-if="form.description" class="text-sm text-gray-600 mt-1">{{ form.description }}</p>
            <div class="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span>{{ form.fields?.length || 0 }} fields</span>
              <span>•</span>
              <span>{{ form.submissions?.length || 0 }} submissions</span>
              <span v-if="form.last_submission" class="text-xs">
                • Last submission: {{ formatDate(form.last_submission.submitted_at) }}
              </span>
              <span v-if="form.expires_at" class="text-xs">
                • Expires: {{ formatDate(form.expires_at) }}
              </span>
            </div>
          </div>

          <div class="flex items-center space-x-2 ml-4">
            <button
              @click="copyFormLink(form)"
              class="px-3 py-1 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded"
              title="Copy form link"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              v-if="lead.email"
              @click="sendFormLink(form, 'email')"
              class="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded"
              title="Email form link to lead"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              v-if="lead.phone"
              @click="sendFormLink(form, 'text')"
              class="px-3 py-1 text-sm bg-teal-50 text-teal-700 hover:bg-teal-100 rounded"
              title="Text form link to lead"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            <Link
              :href="route('leads.intake-forms.edit', [lead.id, form.id])"
              class="px-3 py-1 text-sm bg-green-50 text-green-700 hover:bg-green-100 rounded"
              title="Edit form"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Link>
            <button
              @click="viewFormSubmissions(form)"
              class="px-3 py-1 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded"
              title="View submissions"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              v-if="form.status === 'active'"
              @click="archiveForm(form)"
              class="px-3 py-1 text-sm bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded"
              title="Archive form"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </button>
            <button
              @click="deleteForm(form)"
              class="px-3 py-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded"
              title="Delete form"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { Link } from '@inertiajs/inertia-vue3';
import LoadingSpinner from '@/Components/LoadingSpinner.vue';
import {Inertia} from "@inertiajs/inertia";

export default {
  name: 'LeadIntakeForms',
  components: {
    LoadingSpinner,
    Link,
  },
  props: {
    lead: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const forms = ref([]);
    const templates = ref([]);
    const loading = ref(false);

    const loadForms = async () => {
      loading.value = true;
      try {
        const response = await axios.get(`/leads/${props.lead.id}/intake-forms`);
        forms.value = response.data;
      } catch (error) {
        console.error('Error loading forms:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load forms. Please try again.',
        });
      } finally {
        loading.value = false;
      }
    };

    const loadTemplates = async () => {
      try {
        const response = await axios.get(`/leads/${props.lead.id}/intake-forms-templates`);
        templates.value = response.data;
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };

    const createFromTemplate = async (template) => {
      const { value: name } = await Swal.fire({
        title: 'Create Form from Template',
        text: `Using template: ${template.name}`,
        input: 'text',
        inputLabel: 'Form Name',
        inputValue: template.name,
        inputPlaceholder: 'Enter a name for this form',
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value || value.trim().length < 3) return 'Name must be at least 3 characters';
        },
      });
      if (!name) return;

      try {
        await axios.post(`/leads/${props.lead.id}/intake-forms/create-from-template`, {
          template_id: template.id,
          name: name,
        });
        Swal.fire({ icon: 'success', title: 'Form Created!', timer: 1500, showConfirmButton: false });
        loadForms();
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to create form.' });
      }
    };

    const sendFormLink = async (form, method) => {
      const label = method === 'email' ? 'email' : 'text';
      const leadName = props.lead.first_name || '';

      if (method === 'email') {
        const defaultSubject = form.template?.default_email_subject || 'Please complete this form';
        const defaultMessage = form.template?.default_email_message
          || `Hi${leadName ? ' ' + leadName : ''},\n\nPlease complete the following form at your convenience.`;

        const { value: formValues } = await Swal.fire({
          title: 'Send via email',
          html:
            `<div style="text-align:left">` +
            `<label class="swal2-input-label" style="display:block;margin-bottom:4px;font-weight:500;">Subject</label>` +
            `<input id="swal-subject" class="swal2-input" style="margin:0 0 16px 0;width:100%" value="${defaultSubject.replace(/"/g, '&quot;')}" placeholder="Email subject line">` +
            `<label class="swal2-input-label" style="display:block;margin-bottom:4px;font-weight:500;">Message</label>` +
            `<textarea id="swal-message" class="swal2-textarea" style="margin:0;width:100%" placeholder="Enter your message...">${defaultMessage.replace(/</g, '&lt;')}</textarea>` +
            `</div>`,
          showCancelButton: true,
          confirmButtonText: 'Send email',
          focusConfirm: false,
          preConfirm: () => {
            const subject = document.getElementById('swal-subject').value.trim();
            const message = document.getElementById('swal-message').value.trim();
            if (!subject) { Swal.showValidationMessage('Please enter a subject'); return false; }
            if (!message) { Swal.showValidationMessage('Please enter a message'); return false; }
            return { subject, message };
          },
        });

        if (!formValues) return;

        try {
          await axios.post(`/leads/${props.lead.id}/intake-forms/${form.id}/send-link`, {
            method: 'email',
            message: formValues.message,
            subject: formValues.subject,
          });
          Swal.fire({ icon: 'success', title: 'Sent!', text: 'Form link sent via email.', timer: 2000, showConfirmButton: false });
        } catch (error) {
          Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to send email.' });
        }
      } else {
        const defaultMessage = form.template?.default_sms_message || 'Please complete your form:';

        const { value: message } = await Swal.fire({
          title: 'Send via text',
          input: 'textarea',
          inputLabel: 'Message (the form link will be appended automatically)',
          inputValue: defaultMessage,
          inputPlaceholder: 'Enter your message...',
          showCancelButton: true,
          confirmButtonText: 'Send text',
          inputValidator: (value) => {
            if (!value || value.trim().length === 0) return 'Please enter a message';
          },
        });

        if (!message) return;

        try {
          await axios.post(`/leads/${props.lead.id}/intake-forms/${form.id}/send-link`, { method: 'text', message });
          Swal.fire({ icon: 'success', title: 'Sent!', text: 'Form link sent via text.', timer: 2000, showConfirmButton: false });
        } catch (error) {
          Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to send text.' });
        }
      }
    };

    const copyFormLink = async (form) => {
      try {
        const response = await axios.get(`/leads/${props.lead.id}/intake-forms/${form.id}/link`);
        const link = response.data.url;

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = link;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
          } finally {
            document.body.removeChild(textArea);
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Link Copied!',
          text: 'Form link has been copied to clipboard',
          timer: 2000,
        });
      } catch (error) {
        console.error('Error copying link:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to copy link. Please try again.',
        });
      }
    };

    const viewFormSubmissions = (form) => {
      Inertia.get(route('leads.intake-forms.submissions', [props.lead.id, form.id]));
    };

    const archiveForm = async (form) => {
      const result = await Swal.fire({
        title: 'Archive Form?',
        text: 'Are you sure you want to archive this form? It will no longer be accessible to leads.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, archive it',
      });

      if (result.isConfirmed) {
        try {
          await axios.post(`/leads/${props.lead.id}/intake-forms/${form.id}/archive`);
          Swal.fire('Archived!', 'The form has been archived.', 'success');
          loadForms();
        } catch (error) {
          console.error('Error archiving form:', error);
          Swal.fire('Error', 'Failed to archive form. Please try again.', 'error');
        }
      }
    };

    const deleteForm = async (form) => {
      const result = await Swal.fire({
        title: 'Delete Form?',
        text: 'Are you sure you want to delete this form? This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it',
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`/leads/${props.lead.id}/intake-forms/${form.id}`);
          Swal.fire('Deleted!', 'The form has been deleted.', 'success');
          loadForms();
        } catch (error) {
          console.error('Error deleting form:', error);
          Swal.fire('Error', 'Failed to delete form. Please try again.', 'error');
        }
      }
    };

    const formatDate = (date) => {
      return moment(date).format('MMM D, YYYY h:mm A');
    };

    onMounted(() => {
      loadForms();
      loadTemplates();
    });

    return {
      forms,
      templates,
      loading,
      copyFormLink,
      sendFormLink,
      createFromTemplate,
      viewFormSubmissions,
      archiveForm,
      deleteForm,
      formatDate,
    };
  },
};
</script>
